import { useEffect, useMemo, useRef, useState } from 'react';
import { FilterSection } from '../../components/EligibilityRules/FilterSection';
import { useStartingUniverseStore } from '../../store/useStartingUniverseStore';
import { ToggleBadgeSection } from '../../components/EligibilityRules/ToggleBadgeSection';
import { GICSClassificationsPanel } from '../../components/EligibilityRules/SecurityTypeClassification/GICSClassificationsPanel';
import { GeographicClassificationsPanel } from '../../components/EligibilityRules/SecurityTypeClassification/GeographicClassificationsPanel';
import { FilterExcludedCountBadge } from '../../components/EligibilityRules/FilterExcludedCountBadge';
import { FilterResetButton } from '../../components/EligibilityRules/FilterResetButton';
import {
  GEOGRAPHIC_REGIONS,
  getRegionForCountry,
} from '../../config/constants/geographicRegionsData';
import { exchangeShortName } from '../../config/constants/exchangeShortNames';
import {
  securityTypeCode,
  securityTypeLabel,
} from '../../config/constants/staticClassifications';
import { SelectionBadge } from '@/features/Strategies/components/SelectionBadge';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import type {
  Industry,
  Sector,
  SecurityType,
  SubIndustry,
} from '@biasafe.models';

interface SecurityTypeClassificationProps {
  securityTypeItems: ReadonlyArray<SecurityType>;
  disabledSecurityTypes?: Set<string>;
  securityTypes: {
    excluded: Set<string>;
    toggle: (key: string) => void;
    toggleAll: (keys: Array<string>) => void;
    clearAll: () => void;
    /** Securities the type exclusions remove — the header "N excluded" badge. */
    excludedSecurityCount: number;
  };
  gics: {
    excluded: Set<string>;
    toggle: (codes: Array<string>) => void;
    clear: () => void;
    /**
     * Securities the sector/industry exclusions remove from the universe —
     * NOT the number of codes ticked. Counted with the same three-level rule
     * the backtest engine applies (sector_id / industry_group_id / industry_id
     * as independent NOT IN conditions), so the badge predicts the run.
     */
    excludedSecurityCount: number;
  };
  gicsSectors: ReadonlyArray<Sector>;
  gicsIndustries: ReadonlyArray<Industry>;
  gicsSubIndustries: ReadonlyArray<SubIndustry>;
  /** GICS codes disabled by the Cyclicality selector — visible, not selectable. */
  disabledGicsCodes: Set<string>;
  countries: {
    excluded: Set<string>;
    toggle: (key: string) => void;
    toggleAll: (keys: Array<string>) => void;
    set: (codes: Array<string>) => void;
    clearAll: () => void;
    /** Securities removed by the country exclusions, not countries ticked. */
    excludedSecurityCount: number;
  };
  visibleCountries: ReadonlyArray<{ code: string; label: string }>;
  countriesNotInUniverse?: Set<string>;
  /** What the selected universe holds today — drives the live dot, nothing else. */
  inUniverseSectorCodes?: Set<string>;
  inUniverseIndustryCodes?: Set<string>;
  inUniverseSubIndustryCodes?: Set<string>;
  inUniverseCountries?: Set<string>;
  inUniverseMics?: Set<string>;
  exchanges: {
    excluded: Set<string>;
    toggle: (key: string) => void;
  };
  visibleExchanges: ReadonlyArray<{
    code: string;
    label: string;
    countryCode: string;
  }>;
  /**
   * Mirrors the backend's StructuralRules.primary_share_class_only
   * (see lib/py/quant_engine/.../universe_schemas.py:366). When true,
   * the ingest only retains the primary security for issuers that have
   * multiple share classes or listings — matches Figma's default toggle
   * state. Optional + local-state fallback because the strategy form
   * store doesn't expose structural rules yet; the wiring to the
   * submission payload will land alongside the rest of the structural
   * rules surface in a follow-up.
   */
  primaryShareClassOnly?: boolean;
  /** Rows the Primary-Share-Class rule drops, rendered as a badge on the toggle. */
  primaryShareClassExcludedCount?: number;
  /** Union of the stocks removed by the sub-filters in this card. */
  classificationExcludedCount?: number;
  onPrimaryShareClassChange?: (value: boolean) => void;
}

// ── Shared helper: badge group with show-more ──────────────────────────────

// Collapsed exclusion summaries cap at 2 rows of chips (≈64px: two h-7 badges
// + one gap-2 gap); "Show more" expands to the full set. Measured via
// scrollHeight — mirrors the Figma's max-height approach, not a fixed count.
const COLLAPSED_MAX_H = 64;

function ExcludedBadgeGroup({
  title,
  items,
  onRemove,
}: {
  /** Optional group label. Omitted for single-group summaries (Figma 2686). */
  title?: string;
  /** `code` is the real identity (key/removal); `displayCode`, when present,
   *  is the compact mono prefix shown instead (security-type short codes). */
  items: ReadonlyArray<{ code: string; label: string; displayCode?: string }>;
  onRemove: (code: string) => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (el) setHasMore(el.scrollHeight > COLLAPSED_MAX_H);
  }, [items]);

  if (items.length === 0) return null;

  return (
    <div className="space-y-1.5 pt-1">
      {title && (
        <h4 className="text-[12px] font-semibold text-muted-foreground">
          {title}
        </h4>
      )}
      <div
        ref={containerRef}
        className={cn(
          'flex flex-wrap gap-2 transition-all duration-200',
          !showAll && 'max-h-[64px] overflow-hidden',
        )}
      >
        {items.map((item) => (
          <SelectionBadge
            key={item.code}
            intent="destructive"
            item={item}
            getCode={(i) => i.displayCode ?? i.code}
            getLabel={(i) => i.label}
            onRemove={() => onRemove(item.code)}
          />
        ))}
      </div>
      {(hasMore || showAll) && (
        <button
          type="button"
          onClick={() => setShowAll(!showAll)}
          className="text-xs text-primary hover:text-primary font-medium transition-colors"
        >
          {showAll ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
}

// ── GICS excluded items below sector pills ─────────────────────────────────

function GICSExcludedItems({
  excluded,
  sectors,
  industries,
  subIndustries,
  onRemove,
}: {
  excluded: Set<string>;
  sectors: ReadonlyArray<Sector>;
  industries: ReadonlyArray<Industry>;
  subIndustries: ReadonlyArray<SubIndustry>;
  onRemove: (code: string) => void;
}) {
  const excludedSectors = sectors
    .filter((s) => excluded.has(s.code))
    .map((s) => ({ code: s.code, label: s.label }));
  const excludedIndustries = industries
    .filter((i) => excluded.has(i.code))
    .map((i) => ({ code: i.code, label: i.label }));
  const excludedSubIndustries = subIndustries
    .filter((si) => excluded.has(si.code))
    .map((si) => ({ code: si.code, label: si.label }));

  if (
    excludedSectors.length === 0 &&
    excludedIndustries.length === 0 &&
    excludedSubIndustries.length === 0
  )
    return null;

  return (
    <div className="rounded-md border border-border/50 bg-muted/30 p-3 space-y-3">
      <ExcludedBadgeGroup
        title="Excluded Sectors"
        items={excludedSectors}
        onRemove={onRemove}
      />
      <ExcludedBadgeGroup
        title="Excluded Industry Groups"
        items={excludedIndustries}
        onRemove={onRemove}
      />
      <ExcludedBadgeGroup
        title="Excluded Industries"
        items={excludedSubIndustries}
        onRemove={onRemove}
      />
    </div>
  );
}

// ── Geographic excluded items below region pills ────────────────────────────

function GeographicExcludedItems({
  excluded,
  visibleCountries,
  onSetExcluded,
  excludedExchanges,
  visibleExchanges,
  onRemoveExchange,
}: {
  excluded: Set<string>;
  visibleCountries: ReadonlyArray<{ code: string; label: string }>;
  onSetExcluded: (codes: Array<string>) => void;
  excludedExchanges: Set<string>;
  visibleExchanges: ReadonlyArray<{
    code: string;
    label: string;
    countryCode: string;
  }>;
  onRemoveExchange: (code: string) => void;
}) {
  // Regions where ALL visible countries are excluded
  const fullyExcludedRegions = GEOGRAPHIC_REGIONS.filter((r) => {
    const rc = visibleCountries.filter(
      (c) => getRegionForCountry(c.code) === r.code,
    );
    return rc.length > 0 && rc.every((c) => excluded.has(c.code));
  }).map((r) => ({ code: r.code, label: r.label }));

  // All excluded countries (includes those in a fully-excluded region)
  const excludedCountries = visibleCountries.filter((c) => excluded.has(c.code));

  // Excluded exchanges = exchanges whose parent country is excluded (implied by
  // the region/country exclusion — the full Region→Country→Exchange chain, as
  // the Figma shows) ∪ standalone per-exchange exclusions.
  const excludedExchangeItems = visibleExchanges
    .filter((e) => excluded.has(e.countryCode) || excludedExchanges.has(e.code))
    .map((e) => ({ code: e.code, label: exchangeShortName(e.code) }));

  if (
    fullyExcludedRegions.length === 0 &&
    excludedCountries.length === 0 &&
    excludedExchangeItems.length === 0
  )
    return null;

  const removeRegion = (regionCode: string) => {
    const regionCodes = visibleCountries
      .filter((c) => getRegionForCountry(c.code) === regionCode)
      .map((c) => c.code);
    onSetExcluded(Array.from(excluded).filter((c) => !regionCodes.includes(c)));
  };

  const removeCountry = (countryCode: string) => {
    onSetExcluded(Array.from(excluded).filter((c) => c !== countryCode));
  };

  // Removing an exchange delegates to the hierarchy-aware toggle: an implied
  // one (parent country excluded) demotes the country to "partial" and keeps
  // its siblings; an explicit one is just cleared. (Same as the panel.)

  return (
    <div className="rounded-md border border-border/50 bg-muted/30 p-3 space-y-3">
      <ExcludedBadgeGroup
        title="Excluded Regions"
        items={fullyExcludedRegions}
        onRemove={removeRegion}
      />
      <ExcludedBadgeGroup
        title="Excluded Countries"
        items={excludedCountries}
        onRemove={removeCountry}
      />
      <ExcludedBadgeGroup
        title="Excluded Exchanges"
        items={excludedExchangeItems}
        onRemove={onRemoveExchange}
      />
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────────────────

export function SecurityTypeClassification({
  securityTypeItems,
  disabledSecurityTypes,
  securityTypes,
  gics,
  gicsSectors,
  gicsIndustries,
  gicsSubIndustries,
  disabledGicsCodes,
  countries,
  visibleCountries,
  countriesNotInUniverse,
  inUniverseSectorCodes,
  inUniverseIndustryCodes,
  inUniverseSubIndustryCodes,
  inUniverseCountries,
  inUniverseMics,
  exchanges,
  visibleExchanges,
  primaryShareClassOnly,
  primaryShareClassExcludedCount = 0,
  classificationExcludedCount = 0,
  onPrimaryShareClassChange,
}: SecurityTypeClassificationProps) {
  // One set for the panel: the three GICS levels share a namespace by width,
  // so a single lookup is enough and the row does not need to know its level.
  const liveGicsCodes = useMemo(
    () =>
      new Set([
        ...(inUniverseSectorCodes ?? []),
        ...(inUniverseIndustryCodes ?? []),
        ...(inUniverseSubIndustryCodes ?? []),
      ]),
    [inUniverseSectorCodes, inUniverseIndustryCodes, inUniverseSubIndustryCodes],
  );

  const [gicsView, setGicsView] = useState<'simple' | 'advanced'>('simple');
  const [geoView, setGeoView] = useState<'simple' | 'advanced'>('simple');

  // FilterSection rows are collapsible: `open` reads the store's per-section
  // expanded state (flipped by onToggle); they default open on first mount.
  const toggleSection = useStartingUniverseStore((s) => s.toggleFilterSection);
  const initSection = useStartingUniverseStore((s) => s.initFilterSection);
  const expandedSections = useStartingUniverseStore(
    (s) => s.expandedFilterSectionIds,
  );

  // Seed default open state on first mount.
  useEffect(() => {
    initSection('security_type_classification', false);
  }, [initSection]);

  // Fallback owner for the primary-share-class toggle when no parent wires the
  // value yet. It lives in the StartingUniverse store rather than in local
  // state because the Point-in-time card's constituent count has to react to
  // it, and because a fresh draft has to be able to put it back to its default
  // (OFF) — see resetFilterSections.
  const primaryShareClassValue = primaryShareClassOnly ?? false;
  const handlePrimaryShareClassChange = (next: boolean) => {
    onPrimaryShareClassChange?.(next);
  };

  // Map SecurityType enum strings to { code, label } for ToggleBadgeSection,
  // using the shared label map (Common Stock / Preferred Stock / REIT / …).
  const securityTypeDisplayItems = securityTypeItems.map((st) => ({
    code: st,
    label: securityTypeLabel(st),
  }));

  // Visible regions: only those that have ≥1 visible country
  const visibleRegions = GEOGRAPHIC_REGIONS.filter((r) =>
    visibleCountries.some((c) => getRegionForCountry(c.code) === r.code),
  );

  // Countries and exchanges are two exclusion sets behind one filter header,
  // so an exchange-only exclusion is still an active filter and must offer
  // Reset. Badge and Reset share this flag — gating Reset on countries alone
  // left a header reading "487 excluded" with no way to undo it.
  // (`countries.clearAll` is `clearGeo`: it already empties both sets.)
  const hasGeoExclusions =
    countries.excluded.size > 0 || exchanges.excluded.size > 0;

  const handleRegionToggle = (regionCode: string) => {
    const regionCodes = visibleCountries
      .filter((c) => getRegionForCountry(c.code) === regionCode)
      .map((c) => c.code);
    const allExcluded = regionCodes.every((c) => countries.excluded.has(c));
    if (allExcluded) {
      countries.set(
        Array.from(countries.excluded).filter((c) => !regionCodes.includes(c)),
      );
    } else {
      const next = new Set(countries.excluded);
      for (const c of regionCodes) next.add(c);
      countries.set(Array.from(next));
    }
  };

  // Counter for the outer wrapper. Figma 2580 shows the wrapper with no
  // badge, but exposing the count keeps the visual rhythm of the three
  // sibling Eligibility cards.
  //
  // This is the UNION of the STOCKS the sub-filters below remove — Primary
  // Share Class Only included, since that toggle lives inside this card —
  // counted once each. Not, as it used to be, the summed sizes of the
  // excluded-CODE sets.
  // That old number was wrong in three ways at once: it counted codes rather
  // than securities (excluding one sector read as "1 excluded" whatever its
  // constituent count), it double-counted every name failing more than one
  // sub-filter, and it added `exchanges.excluded` even though exchange
  // exclusions are not applied to the population at all.

  return (
    <div className="space-y-2.5">
      {/* Figma UniverseSelection.tsx line 2580 wraps Primary Share
          Class Only, Security Types, GICS, and Geographic in a
          parent "Security Type & Classification" Card. This wrapper
          makes the three Eligibility sub-cards visually parallel —
          same treatment as Liquidity & Investability and ESG &
          Mandate Screens. */}
      <FilterSection
        title="Security Type & Classification"
        excludedCount={classificationExcludedCount}
        open={expandedSections['security_type_classification'] ?? false}
        onToggle={() => toggleSection('security_type_classification')}
      >
        {/* Figma spaces these four rows (Primary Share / Security Types /
          GICS / Geographic) with mb-[25px] each — match that rhythm. */}
      <div className="space-y-[25px]">
      {/* Primary Share Class Only — matches Figma's first row inside
          Security Type & Classification. The label / description sit
          left, the On/Off label + Switch sit right. Default is true
          to mirror StructuralRules.primary_share_class_only = True. */}
      <div className="flex items-center justify-between py-1.5">
        <div className="flex flex-col gap-0.5">
          <span className="text-muted-foreground font-medium text-[15px]">
            Primary Share Class Only
          </span>
          <span className="text-muted-foreground text-[14px]">
            Retain only the primary security for issuers with multiple share
            classes or listings.
          </span>
        </div>
        <div className="flex items-center gap-2 px-2.5">
          {/* How many rows the rule actually drops, so the toggle isn't a
              blind switch. Sits left of the On/Off pair and only while the
              rule is active — when it's off, nothing is being excluded. */}
          {primaryShareClassValue && !!primaryShareClassExcludedCount && (
            <span className="text-xs px-2 py-0.5 rounded-md border border-destructive/20 bg-destructive/5 text-destructive/80 font-medium whitespace-nowrap">
              {primaryShareClassExcludedCount.toLocaleString()} excluded
            </span>
          )}
          <Label
            htmlFor="primary-share-class-toggle"
            className="text-sm cursor-pointer"
          >
            {primaryShareClassValue ? 'On' : 'Off'}
          </Label>
          <Switch
            id="primary-share-class-toggle"
            checked={primaryShareClassValue}
            onCheckedChange={handlePrimaryShareClassChange}
          />
        </div>
      </div>

      {/* Security Types — toggle pills + an excluded summary below them
          (Figma UniverseSelection.tsx:2684), mirroring the GICS / Geographic
          summary blocks. */}
      <div className="space-y-2">
        <ToggleBadgeSection
          label="Security Types"
          items={securityTypeDisplayItems}
          excluded={securityTypes.excluded}
          disabledCodes={disabledSecurityTypes}
          onToggle={securityTypes.toggle}
          onExcludeAll={(codes) => securityTypes.toggleAll(codes)}
          onIncludeAll={securityTypes.clearAll}
          onReset={() => securityTypes.clearAll()}
          excludedSecurityCount={securityTypes.excludedSecurityCount}
          minIncluded={1}
        />
        {(() => {
          const excludedSecurityTypeItems = securityTypeDisplayItems
            .filter((st) => securityTypes.excluded.has(st.code))
            // Compact mono prefix only — `code` stays the full enum for removal.
            .map((st) => ({ ...st, displayCode: securityTypeCode(st.code) }));
          if (excludedSecurityTypeItems.length === 0) return null;
          return (
            <div className="rounded-md border border-border/50 bg-muted/30 p-3">
              <ExcludedBadgeGroup
                title="Excluded Security Types"
                items={excludedSecurityTypeItems}
                onRemove={(code) => securityTypes.toggle(code)}
              />
            </div>
          );
        })()}
      </div>

      {/* ── GICS: Sectors, Industry Groups, & Industries ── */}
      <div className="space-y-2">
        {gicsView === 'simple' ? (
        <div className="space-y-2">
          {/* Header — Reset sits right of the title (Figma); count + view
              toggle on the right. */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[15px] font-medium text-muted-foreground truncate">
                Sectors, Industry Groups, & Industries
              </span>
              {gics.excluded.size > 0 && (
                <FilterResetButton onClick={() => gics.clear()} />
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {gics.excluded.size > 0 && (
                <FilterExcludedCountBadge count={gics.excludedSecurityCount} />
              )}
              <button
                type="button"
                onClick={() => setGicsView('advanced')}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Advanced View
              </button>
            </div>
          </div>

          {/* Sector pills */}
          <div className="flex flex-wrap gap-1.5">
            {gicsSectors.map((sector) => {
              const isDisabled = disabledGicsCodes.has(sector.code);
              const isExcluded = gics.excluded.has(sector.code);
              return (
                <Badge
                  key={sector.code}
                  variant="outline"
                  className={cn(
                    'transition-colors select-none h-7 px-3 text-xs inline-flex items-center gap-1.5',
                    isDisabled
                      ? 'border-border/60 bg-muted/30 text-muted-foreground/70 cursor-not-allowed'
                      : isExcluded
                        ? 'opacity-60 hover:bg-muted/50 cursor-pointer'
                        : 'border-primary/30 bg-primary/10 text-primary hover:bg-primary/15 cursor-pointer',
                  )}
                  onClick={() => !isDisabled && gics.toggle([sector.code])}
                >
                  {sector.label}
                </Badge>
              );
            })}
          </div>

        </div>
        ) : (
          <GICSClassificationsPanel
            liveCodes={liveGicsCodes}
            excluded={gics.excluded}
            disabledCodes={disabledGicsCodes}
            onToggle={gics.toggle}
            sectors={gicsSectors}
            industries={gicsIndustries}
            subIndustries={gicsSubIndustries}
            onSwitchToSimple={() => setGicsView('simple')}
            onReset={() => gics.clear()}
            excludedSecurityCount={gics.excludedSecurityCount}
          />
        )}

        {/* Excluded items — the exclusion summary shows in BOTH the simple and
            advanced views. Reset lives in the filter header (right of title). */}
        <GICSExcludedItems
          excluded={gics.excluded}
          sectors={gicsSectors}
          industries={gicsIndustries}
          subIndustries={gicsSubIndustries}
          onRemove={(code) => gics.toggle([code])}
        />
      </div>

      {/* ── Geographic: Regions, Countries, & Exchanges ── */}
      <div className="space-y-2">
        {geoView === 'simple' ? (
        <div className="space-y-2">
          {/* Header — Reset sits right of the title (Figma); count + view
              toggle on the right. */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-[15px] font-medium text-muted-foreground truncate">
                Regions, Countries, & Exchanges
              </span>
              {hasGeoExclusions && (
                <FilterResetButton onClick={countries.clearAll} />
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {hasGeoExclusions && (
                <FilterExcludedCountBadge
                  count={countries.excludedSecurityCount}
                />
              )}
              <button
                type="button"
                onClick={() => setGeoView('advanced')}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                Advanced View
              </button>
            </div>
          </div>

          {/* Region pills */}
          <div className="flex flex-wrap gap-1.5">
            {visibleRegions.map((region) => {
              const regionCodes = visibleCountries
                .filter((c) => getRegionForCountry(c.code) === region.code)
                .map((c) => c.code);
              const isDisabled =
                regionCodes.length > 0 &&
                regionCodes.every((c) => countriesNotInUniverse?.has(c));
              const allExcluded =
                regionCodes.length > 0 &&
                regionCodes.every((c) => countries.excluded.has(c));
              return (
                <Badge
                  key={region.code}
                  variant="outline"
                  className={cn(
                    'transition-colors select-none h-7 px-3 text-xs inline-flex items-center gap-1.5',
                    isDisabled
                      ? 'border-border/60 bg-muted/30 text-muted-foreground/70 cursor-not-allowed'
                      : allExcluded
                        ? 'opacity-60 hover:bg-muted/50 cursor-pointer'
                        : 'border-primary/30 bg-primary/10 text-primary hover:bg-primary/15 cursor-pointer',
                  )}
                  onClick={() => !isDisabled && handleRegionToggle(region.code)}
                >
                  {region.label}
                </Badge>
              );
            })}
          </div>

        </div>
        ) : (
          <GeographicClassificationsPanel
            liveCountries={inUniverseCountries}
            liveMics={inUniverseMics}
            visibleCountries={visibleCountries}
            excludedCountries={countries.excluded}
            countriesNotInUniverse={countriesNotInUniverse}
            onSetExcluded={countries.set}
            visibleExchanges={visibleExchanges}
            excludedExchanges={exchanges.excluded}
            onToggleExchange={exchanges.toggle}
            onSwitchToSimple={() => setGeoView('simple')}
            onReset={countries.clearAll}
            excludedSecurityCount={countries.excludedSecurityCount}
          />
        )}

        {/* Excluded items — the exclusion summary shows in BOTH views. Reset
            lives in the filter header (right of title). */}
        <GeographicExcludedItems
          excluded={countries.excluded}
          visibleCountries={visibleCountries}
          onSetExcluded={countries.set}
          excludedExchanges={exchanges.excluded}
          visibleExchanges={visibleExchanges}
          onRemoveExchange={(code) => exchanges.toggle(code)}
        />
      </div>
        </div>
      </FilterSection>
    </div>
  );
}
